import { createCmsClient } from "@9amhealth/cms-api";
import envVariables from "src/lib/envVariables";
import Language from "src/constants/language";
import { EducationalContent } from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";

export interface Journey {
  journey: string;
  date: string;
}

interface ClassContent {
  language: string;
  name: string;
  listOfEducationalContent: { _ref: string; _key: string; _type: string }[];
  _createdAt: string;
  _id: string;
  _rev: string;
  _type: string;
  _updatedAt: string;
}

export interface JourneyContent {
  name: string;
  language: string;
  type: string;
  interval: number;
  listOfClasses: ClassContent[];
}

export enum JourneyName {
  default = "Default"
}

export enum MedicalCategory {
  diabetes = "diabetes",
  hypertension = "hypertension",
  weightLoss = "weight-loss"
}

export enum DefaultCategory {
  all = "all",
  nutrition = "nutrition",
  fitness = "fitness",
  mentalHealth = "mental-health",
  heartHealth = "heart-health"
}

export type Category = DefaultCategory | MedicalCategory;

export const categoryIds: Record<Category, string> = {
  [DefaultCategory.all]: "",
  [DefaultCategory.nutrition]: "51f65264-13eb-4b9e-bd5c-a4ded6b86125",
  [DefaultCategory.fitness]: "ca61da00-d9d5-4dbd-ab17-1d897cdcc87c",
  [DefaultCategory.heartHealth]: "9ca25205-008f-49af-9283-9579b39799bb",
  [DefaultCategory.mentalHealth]: "91acc25f-67db-45f7-8631-8bb952a35496",
  [MedicalCategory.diabetes]: "6c04b2a7-9a90-4a95-81e9-ebb82e6e6b02",
  [MedicalCategory.hypertension]: "fc7a9524-f7f4-47e7-960f-1da802f0da91",
  [MedicalCategory.weightLoss]: "f19031c8-58ff-4375-bbe5-45ba1b2e56ce"
};

export const getClassNumber = (
  enrollmentDate: string,
  journeyInterval: number
) => {
  const today = new Date();
  const enrollmentDateFormatted = new Date(enrollmentDate);
  const difference = today.getTime() - enrollmentDateFormatted.getTime();
  const daysPassed = Math.floor(difference / (1000 * 60 * 60 * 24));
  return parseInt((daysPassed / journeyInterval).toString());
};

export const getCategoriesIds = (categories: Category[]): string[] => {
  const ids: string[] = [];
  categories.forEach((category) => ids.push(categoryIds[category]));
  return ids;
};

export const getJourneyContent = async (
  userJourney = JourneyName.default,
  language = Language.en
) => {
  const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
  const res = await cmsClient.api.educational.journey.$get({
    query: { name: userJourney, language }
  });
  if (!res.ok) {
    throw new Error(`journey failed: ${res.status}`);
  }
  const { items } = await res.json();
  return items as unknown as JourneyContent[];
};

export const getEducationalContentByJourneyAndCategories = async (options: {
  categories: Category[];
  language: Language;
  userClassNumber?: number;
}): Promise<{
  cmsItems: EducationalContent[];
}> => {
  const journeyContent = await getJourneyContent(
    JourneyName.default,
    options.language
  );

  const classEducationalItems = journeyContent[0].listOfClasses.find(
    (c) => c.name === `Batch ${options.userClassNumber}`
  )?.listOfEducationalContent;

  const educationalItemsRefs =
    classEducationalItems?.map((item) => item._ref) ?? [];

  const categoriesIds = getCategoriesIds(options.categories);

  const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
  const res = await cmsClient.api.educational["by-categories"].$get({
    query: {
      categoryIds: categoriesIds.join(","),
      language: options.language,
      itemIds: educationalItemsRefs.join(",")
    }
  });
  if (!res.ok) {
    throw new Error(`by-categories failed: ${res.status}`);
  }
  const { items } = await res.json();
  const cmsItems = items as unknown as EducationalContent[];

  // order cms items by order in educationalItems
  const orderedCmsItems = cmsItems.toSorted((a, b) => {
    const aIndex = educationalItemsRefs.indexOf(a._id);
    const bIndex = educationalItemsRefs.indexOf(b._id);
    return aIndex - bIndex;
  });

  return { cmsItems: orderedCmsItems };
};

export const getEducationalContent = async (
  categories: Category[],
  language = Language.en
) => {
  const categoriesIds = getCategoriesIds(categories);

  const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
  const res = await cmsClient.api.educational["by-categories"].$get({
    query: { categoryIds: categoriesIds.join(","), language }
  });
  if (!res.ok) {
    throw new Error(`by-categories failed: ${res.status}`);
  }
  const { items } = await res.json();
  const cmsItems = items as unknown as EducationalContent[];

  return { cmsItems };
};

export default getEducationalContent;
